import React from "react";
import { Check, X, CreditCard, Banknote, Send, KeyRound, Loader2, Ban } from "lucide-react";

const METHOD_LABELS = {
  stripe: { label: "Stripe", Icon: CreditCard, color: "text-blue-700 bg-blue-50" },
  western_union: { label: "Western Union", Icon: Banknote, color: "text-amber-700 bg-amber-50" },
  money_gram: { label: "MoneyGram", Icon: Banknote, color: "text-rose-700 bg-rose-50" },
  remitly: { label: "Remitly", Icon: Send, color: "text-emerald-700 bg-emerald-50" },
};

const STATUS_LABELS = {
  pending_review: { label: "Pendiente de revisión", color: "bg-amber-100 text-amber-800" },
  auto_granted: { label: "Auto-acreditado · revisar", color: "bg-blue-100 text-blue-800" },
  confirmed: { label: "Confirmado", color: "bg-emerald-100 text-emerald-800" },
  suspended: { label: "Suspendido", color: "bg-red-100 text-red-800" },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-800" },
  initiated: { label: "Iniciado (Stripe)", color: "bg-zinc-100 text-zinc-700" },
  completed: { label: "Completado", color: "bg-emerald-100 text-emerald-800" },
};

export default function PaymentsTable({ payments, loading, onConfirm, onReject, onSuspend }) {
  if (loading) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (payments.length === 0) {
    return <div className="p-10 text-center text-zinc-500 text-sm">No hay transacciones con ese filtro.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-zinc-600 text-[10px] uppercase tracking-widest font-black">
          <tr>
            <th className="text-left p-3">Fecha</th>
            <th className="text-left p-3">Usuario</th>
            <th className="text-left p-3">PIN</th>
            <th className="text-left p-3">Plan</th>
            <th className="text-left p-3">Método</th>
            <th className="text-left p-3">Referencia</th>
            <th className="text-right p-3">Monto</th>
            <th className="text-left p-3">Estado</th>
            <th className="text-right p-3">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {payments.map((p) => (
            <PaymentRow key={p.id} payment={p} onConfirm={onConfirm} onReject={onReject} onSuspend={onSuspend} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentRow({ payment: p, onConfirm, onReject, onSuspend }) {
  const M = METHOD_LABELS[p.method] || { label: p.method, Icon: CreditCard, color: "text-zinc-700 bg-zinc-100" };
  const S = STATUS_LABELS[p.status] || { label: p.status, color: "bg-zinc-100" };
  const { Icon } = M;

  return (
    <tr data-testid={`admin-payment-row-${p.id}`} className="hover:bg-zinc-50">
      <td className="p-3 text-xs text-zinc-700 whitespace-nowrap">
        {new Date(p.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
      </td>
      <td className="p-3 font-bold text-zinc-900">{p.username}</td>
      <td className="p-3">
        {p.user_pin ? (
          <span className="inline-flex items-center gap-1 font-mono font-black tracking-widest text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
            <KeyRound className="w-3 h-3" />
            {p.user_pin}
          </span>
        ) : (
          <span className="text-xs text-zinc-400">—</span>
        )}
      </td>
      <td className="p-3">
        {p.plan_name}
        {p.extra_call_type ? ` · ${p.extra_call_type === "emergency" ? "Emergencia" : "Detención"}` : ""}
      </td>
      <td className="p-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full ${M.color}`}>
          <Icon className="w-3 h-3" />
          {M.label}
        </span>
      </td>
      <td className="p-3 font-mono text-xs text-zinc-700 max-w-xs truncate" title={p.reference || p.mtcn || p.session_id || ""}>
        {p.reference || p.mtcn || p.session_id || "—"}
      </td>
      <td className="p-3 text-right font-bold text-zinc-900">
        ${parseFloat(p.amount_deposited || 0).toFixed(2)}
        {p.amount_expected !== p.amount_deposited && (
          <div className="text-[10px] font-normal text-amber-700">
            Esperado: ${parseFloat(p.amount_expected || 0).toFixed(2)}
          </div>
        )}
      </td>
      <td className="p-3">
        <span className={`inline-block text-[10px] font-black uppercase px-2 py-1 rounded-full ${S.color}`}>
          {S.label}
        </span>
      </td>
      <td className="p-3 text-right">
        {(p.status === "pending_review" || p.status === "auto_granted") && (
          <div className="flex items-center gap-1.5 justify-end">
            <button
              data-testid={`admin-confirm-${p.id}`}
              onClick={() => onConfirm(p.id)}
              className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center"
              title="Confirmar pago"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              data-testid={`admin-suspend-${p.id}`}
              onClick={() => onSuspend?.(p)}
              className="w-8 h-8 rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center justify-center"
              title="Suspender servicio"
            >
              <Ban className="w-4 h-4" />
            </button>
            {p.status === "pending_review" && (
              <button
                data-testid={`admin-reject-${p.id}`}
                onClick={() => onReject(p.id)}
                className="w-8 h-8 rounded-lg bg-zinc-700 hover:bg-zinc-800 text-white flex items-center justify-center"
                title="Rechazar (sin suspender)"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
